import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext';
import { useModifier } from '../contexts/ModifiersContext';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { productNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import AddCard from '../components/AddCard';
import Layout2 from '../components/Layout2';
import PageHeader from '../components/PageHeader';
import AddModifierGroup from '../components/AddModifierGroup';
import InfiniteScroll from 'react-infinite-scroll-component';

function ModifierGroupList() {
    const { toast, restaurantId, auth, hasPermission } = useAuth();
    const { getAllModifierGroups, getSingleModifier } = useModifier()

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);
    
    const [modifiersList, setModifiersList] = useState([])
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editModifierId, setEditModifierId] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [modifierDetail, setModifierDetail] = useState({
        name: "",
        display_name: "",
        price: 0,
        modifiers: [],
        multi_select: false,
        min: null,
        max: null,
        no_max: false,
        restaurant_id: restaurantId,
        auto_popup: false,
    })
    const [filter, setFilter] = useState({
        filter: null,
        limit: 50,
        totalRenderedRecords: 0,
        restaurant_id: restaurantId,
    });

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
        }
    }, [restaurantId])

    const handleFilterChange = async (newFilter) => {
        setModifiersList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0, });
        setHasMore(true);
    };

    useEffect(() => {
        if (filter) {
            fetchModifiers(filter)
        }
    }, [filter])

    const fetchModifiers = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true)

                const data = await getAllModifierGroups(filter)
                if (data.modifierGroups.length !== 0 && data.totalCount !== modifiersList.length) {
                    setModifiersList([...modifiersList, ...data.modifierGroups])
                    setTotalRecords(data.totalCount)
                    setTotalRenderedRecords(modifiersList.length)
                }

                setIsLoading(false)
            }
        } catch (error) {
            console.error('Error fetching modifier groups', error);
        }
    }

    const handleCardClick = (id) => {
        if(!canUpdate && !canDelete) return;

        setEditMode(true);
        setEditModifierId(id);
        setVisible(false);
    };

    const handleLoadMore = () => {
        if (modifiersList.length === totalRecords) {
            setHasMore(false);
        } else if (modifiersList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchModifierGroupProps = async (newModifierGroupId) => {
        try {
            if (editMode && editModifierId) {
                const modifierGroup = await getSingleModifier(editModifierId);
                const modifierGroupIndex = modifiersList.findIndex((c) => c.id === editModifierId);

                if (modifierGroupIndex !== -1) {
                    if (!modifierGroup) {
                        const updatedModifierGroupList = [...modifiersList];
                        updatedModifierGroupList.splice(modifierGroupIndex, 1);

                        setModifiersList(updatedModifierGroupList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedModifierGroupList = [...modifiersList];
                        updatedModifierGroupList[modifierGroupIndex] = { ...modifierGroup, itemsCount: modifierGroup.modifiers.length };

                        setModifiersList(updatedModifierGroupList);
                    }
                } else {
                    setModifiersList([...modifiersList, modifierGroup]);
                }
            } else if (visible) {
                const updatedModifierGroupList = [...modifiersList];
                updatedModifierGroupList.unshift({ ...modifierDetail, id: newModifierGroupId, itemsCount: modifierDetail.modifiers.length });

                setModifiersList(updatedModifierGroupList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching modifier groups.', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];
        let count = 0;

        modifiersList.forEach((modifier, index) => {
            currentRow.push(
                <div key={modifier.id} className="d-flex">
                    <Card
                        header={
                            <>
                                <div className='modifier-name' title={modifier.name}>
                                    {modifier.name}
                                </div>
                                <div className='modifier-options'>
                                    {modifier.itemsCount ? modifier.itemsCount : 0} option(s)
                                </div>
                            </>}
                        className="modifiers-list-card"
                        onClick={() => handleCardClick(modifier.id)}
                    />
                </div>
            );

            count++;
            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
            count = 0;
        });

        return rows;
    };

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                {/* <div className='d-flex flex-row px-4 py-2 w-100 border-bottom'>
                    <h4>Modifier Group</h4>
                </div> */}
                <PageHeader title={'Modifier Group'} />
                <div className='px-4 py-2'>
                    {
                        (visible || (editMode && editModifierId)) &&
                        (
                            <AddModifierGroup
                                id={editModifierId}
                                visible={visible}
                                editModifierId={editModifierId}
                                editMode={editMode}
                                modifierDetail={modifierDetail}
                                setEditModifierId={setEditModifierId}
                                setVisible={setVisible}
                                setEditMode={setEditMode}
                                fetchModifiers={fetchModifierGroupProps}
                                setModifierDetail={setModifierDetail}
                                canCreate={canCreate}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                            />
                        )
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <Toast ref={toast} />
                            <div className='user-list'>
                                <InfiniteScroll
                                    dataLength={modifiersList.length}
                                    next={handleLoadMore}
                                    hasMore={hasMore}
                                    className="card-container"
                                >
                                    {canCreate && <AddCard color="#536347" label="Add Modifier Group" setVisible={setVisible} />}
                                    {modifiersList.length !== 0 && (
                                        renderCardsInRows()
                                    )}
                                </InfiniteScroll>

                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default ModifierGroupList